// Port of Flutter's devicesScreen.dart — a room's devices.
//
//   AppBar  avatar · room name · location name
//   body    the same common-devices strip as the Rooms screen, then the SENSOR
//           strip, then the room's device grid (maxCrossAxisExtent 250, aspect
//           0.9, spacing 10)
//   FAB     Add Device
//
// Like the Dart, opening a device subscribes its `<uno>/client` topic first so
// live status is already flowing by the time the control screen builds.
//
// ⚠️ TWO THINGS THE LIST DOES BEFORE IT DRAWS, and both were missing:
//
//   sort    every row carries the customer's own `SortOrder`; the desktop shell
//           sorted by it and the phone did not, so the same room read in two
//           different orders. Worse on this screen than most, because the room
//           reply is COMPLETED from the location reply (core/devices/roomMerge)
//           and those rows are appended — a merged-in device landed last
//           whatever its SortOrder said.
//   split   sensors have no outputs and belong in their own row above the grid;
//           see core/devices/sensorState.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import WifiIcon from '@mui/icons-material/Wifi';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ProfileTitleBar } from '../components/MobileAppBar';
import CommonDevicesStrip from '../components/CommonDevicesStrip';
import SensorStrip from '../components/SensorStrip';
import DeviceCard from '../components/DeviceCard';
import ExtentGrid from '../components/ExtentGrid';
import CircleMenu from '../components/CircleMenu';
import UpdateCodeDialog from '../../shared/UpdateCodeDialog';
import WifiFleetDialog from '../../shared/WifiFleetDialog';
import { useApp } from '../../../core/state/AppContext';
import { getDevicesByRoom, getCommonDevices, updateDeviceSortOrder } from '../../../core/api/api';
import { sortByOrder } from '../../../core/utils/sortOrder';
import { applyDeviceOrder, saveDeviceOrder } from '../../../core/utils/orderCache';
import { arrayMove } from '../../../core/utils/reorder';
import ReorderArrows, { EditOrderButton } from '../components/ReorderBar';
import { splitSensors } from '../../../core/devices/sensorState';
import { useHideOffline, useOnlineOnly } from '../../../core/hooks/useHideOffline';
import { subscribeTopic } from '../../../core/mqtt/mqttClient';
import { drillFromDevices, arrivedViaBack, parentOf } from '../../../core/nav/appNav';

export default function MobileDevicesScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { roomId } = useParams();
  const { state } = useLocation();
  const {
    userId, toggleFavourite, canUpdateFirmware, canReorder, editOrder, setEditOrder,
    showCommon, setShowCommon,
  } = useApp();

  const [devices, setDevices] = useState(null);
  const [common, setCommon] = useState([]);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [wifiOpen, setWifiOpen] = useState(false);

  const roomName = state?.roomName || 'Room';
  const locationName = state?.locationName || '';
  const locationId = state?.locationId;

  const load = useCallback(async () => {
    if (!userId) return;
    const [ds, cs] = await Promise.all([
      getDevicesByRoom(userId, roomId, locationId).catch(() => null),
      locationId ? getCommonDevices(userId, locationId).catch(() => null) : Promise.resolve(null),
    ]);
    // Backend SortOrder first, then the local reorder override — the same two
    // steps, in the same order, as DesktopShell. A drag done in a wide window
    // has to survive the window being narrowed into the mobile shell.
    const rows = sortByOrder(Array.isArray(ds?.data) ? ds.data : []);
    setDevices(applyDeviceOrder(userId, roomId, rows, (d) => d.UniqueNumber));
    setCommon(sortByOrder(Array.isArray(cs?.data) ? cs.data : []));
  }, [userId, roomId, locationId]);

  useEffect(() => { load(); }, [load]);

  /*
   * "Hide offline devices" — the SAME setting the desktop reads, honoured here
   * too. It is one localStorage key for both shells, and only one of them was
   * applying it (core/hooks/useHideOffline).
   *
   * Filtered before the sensor split so a hidden sensor is counted once, in the
   * same place as a hidden switch.
   */
  const [hideOffline] = useHideOffline();

  // ⚠️ REORDERING SUSPENDS BOTH THE FILTER AND THE SENSOR SPLIT, and it has to.
  // The grid normally draws `controls` — this room's devices minus the offline
  // ones minus the sensors, which are lifted into their own strip. That list's
  // indices do not correspond to the room's, so moving "the 2nd card" would
  // persist position 2 of a list the operator is not looking at, silently
  // reordering the hidden and the lifted-out rows around it.
  //
  // So while editing, the grid shows the room exactly as it is stored: every
  // device, sensors included, in one list — which is also the only honest way
  // to choose an order, since that IS the thing being ordered. DesktopShell
  // suspends its own filter for the same reason.
  const reordering = editOrder && canReorder;
  const effectiveHideOffline = hideOffline && !reordering;
  // Memoised rather than `devices || []` inline: the hook's RESULT is memoised on
  // the list's identity, so a fresh array literal every render would defeat it
  // and rebuild the sensor split on each pass.
  const deviceList = useMemo(() => devices || [], [devices]);
  const { devices: liveCommon, hiddenCount: hiddenCommon } = useOnlineOnly(common, effectiveHideOffline);
  const { devices: liveDevices, hiddenCount: hiddenInRoom } = useOnlineOnly(deviceList, effectiveHideOffline);

  // Persist a new device order: the local cache AND the backend SortOrder.
  // ⚠️ BOTH, because the room list is a merge — devices recovered from the
  // location reply (core/devices/roomMerge) carry no `ID`, so there is nothing
  // to write SortOrder against and only the cache can hold their position.
  // Exactly what DesktopShell does; the two shells share the cache.
  const moveDevice = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= deviceList.length) return;
    const next = arrayMove(deviceList, i, j);
    setDevices(next.map((d, k) => ({ ...d, SortOrder: k })));
    saveDeviceOrder(userId, roomId, next.map((d) => d.UniqueNumber));
    await Promise.all(next.map((d, k) => (d.ID != null
      ? updateDeviceSortOrder(userId, d.ID, k).catch(() => null) : null)));
  };

  // Sensors out of the grid and into their own row. `devices` stays null while
  // loading, and null is not an empty room — the grid below tells them apart.
  const { sensors, controls } = useMemo(
    () => splitSensors(liveDevices),
    [liveDevices],
  );
  const commonSplit = useMemo(() => splitSensors(liveCommon), [liveCommon]);

  // What the grid actually draws: the controls normally, the room exactly as
  // stored while reordering. See the note on `reordering` above — the indices
  // the arrows use are indices into THIS list, and it has to be the full one.
  const gridDevices = reordering ? deviceList : controls;
  // One SENSORS row, not two: a location's roomless sensors and this room's own
  // read the same to the person looking at them.
  // ⚠️ The LOCATION's sensors follow the common-devices toggle, this room's do
  // not. They are merged into one row because they read the same to the person
  // looking at them — but they are not the same thing to the setting: hiding
  // "common devices" and still being shown the location's roomless sensors
  // would look like the toggle only half worked.
  const allSensors = useMemo(
    () => (showCommon ? [...commonSplit.sensors, ...sensors] : sensors),
    [commonSplit.sensors, sensors, showCommon],
  );

  // ── One device: show it, not a grid of one ──────────────────────────────
  //
  // The end of the drill. `/device/:uno` renders whatever the DeviceID calls
  // for, so a lone DeviceID 49 lands on the four-touch switches with nothing
  // else to tap through — which is the whole point of the chain.
  //
  // ⚠️ The common strip above the grid counts too: one device in this room plus
  // one common device at this location is two things on the screen.
  //
  // Not on a back, and not before the lists have landed — see RoomsScreen.
  const drilled = useRef(false);
  useEffect(() => {
    if (devices == null || drilled.current || arrivedViaBack(state)) return;
    const target = drillFromDevices({
      devices, common, roomId, roomName, locationId, locationName,
    });
    if (!target) return;
    drilled.current = true;
    subscribeTopic(`${target.state.device.UniqueNumber}/client`);
    navigate(target.path, { replace: true, state: target.state });
  }, [devices, common, state, navigate, roomId, roomName, locationId, locationName]);

  const goBack = () => {
    const t = parentOf(`/room/${roomId}/devices`, { locationId, locationName });
    if (t) navigate(t.path, { state: t.state });
    else navigate(-1);
  };

  const openDevice = (d) => {
    subscribeTopic(`${d.UniqueNumber}/client`);
    // `from` is what lets the device screen come BACK here correctly: the room
    // row it is handed carries RoomID but no LocationID, and the room list needs
    // the location or it drops device types. See core/devices/deviceNav.js.
    navigate(`/device/${d.UniqueNumber}`, {
      state: { device: d, from: { roomId, roomName, locationId, locationName } },
    });
  };
  const onStar = async (d, fav) => { await toggleFavourite(d, fav); load(); };

  return (
    <Box sx={{ height: '100%', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      {/* Structural, so the arrow and the Android button agree — and so a room
          that was drilled into still has somewhere to go back to. */}
      <ProfileTitleBar
        title={roomName}
        subtitle={locationName}
        onBack={goBack}
        actions={canReorder ? (
          <EditOrderButton
            editing={editOrder}
            onToggle={() => setEditOrder((v) => !v)}
            color={c.text}
          />
        ) : null}
      />

      {/* PINNED — same two-container split as the Rooms screen: the common strip
          stays put and scrolls sideways, only the device grid scrolls down. */}
      {showCommon && (
        <CommonDevicesStrip
          devices={commonSplit.controls}
          hiddenCount={hiddenCommon}
          onOpen={openDevice}
          onToggleFavourite={onStar}
        />
      )}

      {/* Directly after the common devices, and before the grid — except while
          reordering, when the sensors are back in the grid in their real
          positions because that is what is being ordered. */}
      {!reordering && (
        <SensorStrip devices={allSensors} onOpen={openDevice} onToggleFavourite={onStar} />
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Box sx={{ p: 1 }}>
          {/* `devices == null` is still loading; empty is empty. A room that is
              nothing but sensors is NOT empty — "No Devices Found" printed above
              a row of them reads as a fault. Nor is a room the FILTER emptied:
              that one names the checkbox, not the room. */}
          {devices != null && controls.length === 0 && allSensors.length === 0 && (
            <Typography sx={{ color: c.text, textAlign: 'center' }}>
              {hiddenInRoom > 0
                ? `All ${hiddenInRoom} device${hiddenInRoom > 1 ? 's are' : ' is'} offline and hidden`
                : 'No Devices Found'}
            </Typography>
          )}
          {hiddenInRoom > 0 && controls.length > 0 && (
            <Typography sx={{ color: c.text, opacity: 0.65, fontSize: 11, px: 1, pb: 0.5 }}>
              {`${hiddenInRoom} offline ${hiddenInRoom > 1 ? 'devices are' : 'device is'} hidden`}
            </Typography>
          )}
          {gridDevices.length > 0 && (
            <ExtentGrid maxExtent={250} childAspectRatio={0.9} gap={10}>
              {gridDevices.map((d, i) => (
                <Box key={d.UniqueNumber} sx={{ position: 'relative', height: '100%' }}>
                  <DeviceCard
                    device={d}
                    onOpen={openDevice}
                    onToggleFavourite={onStar}
                  />
                  {reordering && (
                    // Overlaid rather than inserted into the card: DeviceCard's
                    // layout is shared with the favourites grid and the common
                    // strip, and giving it a reorder slot would put a gap in
                    // both of those for a mode neither has.
                    <Box sx={{
                      position: 'absolute', top: 4, left: 4, borderRadius: '10px',
                      bgcolor: 'rgba(0,0,0,0.55)', display: 'flex',
                    }}
                    >
                      <ReorderArrows
                        index={i}
                        count={gridDevices.length}
                        onMove={moveDevice}
                        color="#fff"
                      />
                    </Box>
                  )}
                </Box>
              ))}
            </ExtentGrid>
          )}
        </Box>
      </Box>

      <CircleMenu
        items={[
          {
            icon: <AddIcon />,
            title: 'Add Device',
            onClick: () => navigate(`/add-device?locationId=${locationId ?? ''}&roomId=${roomId}`),
          },
          // Onboard a brand-new device: find its setup hotspot and hand it the
          // Wi-Fi it should join. The location travels along so the picker can
          // offer the networks already in use at this site.
          {
            icon: <WifiIcon />,
            title: 'Set Up New Device',
            onClick: () => navigate(`/wifi-setup?locationId=${locationId ?? ''}`),
          },
          // The same one setting as on the Rooms screen and the desktop header.
          {
            icon: <GroupWorkIcon />,
            title: showCommon ? 'Hide common devices' : 'Show common devices',
            onClick: () => setShowCommon((v) => !v),
          },
          // Room-wide firmware push — owner / super admin only.
          ...(canUpdateFirmware ? [{
            icon: <SystemUpdateAltIcon />,
            title: 'Update Code (Room)',
            onClick: () => setUpdateOpen(true),
          }] : []),
          // ⚠️ REBOOTS every device in the room. Distinct from "Set Up New
          // Device" above, which hands ONE unprovisioned device its first
          // network over BLE; this pushes ADDWIFI over MQTT to devices that are
          // already on the air. Same gate as Update Code — it is the same kind
          // of act on the same set of devices.
          ...(canUpdateFirmware ? [{
            icon: <WifiIcon />,
            title: 'Add / remove Wi-Fi (Room)',
            onClick: () => setWifiOpen(true),
          }] : []),
        ]}
      />

      <UpdateCodeDialog
        open={updateOpen}
        scope={{ type: 'room', id: roomId, name: roomName, locationId }}
        onClose={() => setUpdateOpen(false)}
      />

      <WifiFleetDialog
        open={wifiOpen}
        scope={{ type: 'room', id: roomId, name: roomName, locationId }}
        onClose={() => setWifiOpen(false)}
      />
    </Box>
  );
}
